import { CommonUtils } from './CommonUtils';
import rolesPermissionsData from '../(config)/roles-permissions.json';

export type Role = keyof typeof rolesPermissionsData;
export type Permission = string;

// AccessControl is a singleton object. This ensures that
// - only one instance exists per server process
// - efficient memory usage in the browser
// The permissions data is static - /(config)/roles-permissions.json - and is known at build time
// Next.js bundles the JSON file with client code during build. So When the browser loads the 
// application, it already has access to the permissions data
export class AccessControl {
  private static instance: AccessControl;
  private rolesPermissions: Record<string, string[]>;

  private constructor() {
    // Keep only role -> permissions array entries; exclude objects like loanStagePolicies.
    this.rolesPermissions = Object.entries(rolesPermissionsData as Record<string, unknown>).reduce<Record<string, string[]>>((acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  public static getInstance(): AccessControl {
    if (!AccessControl.instance) {
      AccessControl.instance = new AccessControl();
    }
    return AccessControl.instance;
  }

  // If a stage policy exists (e.g. CANCELLED), it overrides role-based permissions.
  private applyLoanStagePolicy(permissions: Permission[], stage?: string | null): Permission[] {
    const normalizedStage = String(stage || '').trim().toUpperCase();
    if (!normalizedStage) {
      return permissions;
    }

    const loanStagePolicies = (rolesPermissionsData as Record<string, any>).loanStagePolicies as Record<string, Permission[]> | undefined;
    const stagePolicyPermissions = loanStagePolicies?.[normalizedStage];

    if (Array.isArray(stagePolicyPermissions)) {
      return stagePolicyPermissions;
    }

    return permissions;
  }

  private getPermissionsForRole(role?: string | null, _stage?: string | null): Permission[] {
    if (!role) {
      return [];
    }

    try {
      // Handle case sensitivity by normalizing the role
      const normalizedRole = this.normalizeRoleName(role);
      // Base permissions come from role mapping before any stage override is applied.
      const basePermissions = this.rolesPermissions[normalizedRole] || [];
      // Apply stage policy (e.g. CANCELLED) to override role permissions when configured.
      return this.applyLoanStagePolicy(basePermissions, _stage);
    } catch (error) {
      CommonUtils.logError(`Error getting permissions for role ${role}:`, error);
      return [];
    }
  }

  public getPermissionsForRoles(roles?: string[] | null, stage?: string | null): Permission[] {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return [];
    }

    try {
      // Create a Set to store unique permissions
      const permissionsSet = new Set<Permission>();
      
      // Add permissions from each role to the set
      roles.forEach(role => {
        const rolePermissions = this.getPermissionsForRole(role, stage);
        rolePermissions.forEach(permission => permissionsSet.add(permission));
      });
      
      // Convert Set back to array
      const combinedPermissions = Array.from(permissionsSet);
      // Final stage-policy pass keeps multi-role flow consistent and enforces stage override.
      return this.applyLoanStagePolicy(combinedPermissions, stage);
    } catch (error) {
      CommonUtils.logError(`Error getting permissions for roles ${roles}:`, error);
      return [];
    }
  }

  public hasPermission(role: string | null | undefined, permission: Permission, _stage?: string | null): boolean {
    if (!role || !permission) {
      return false;
    }

    // Use stage-aware permissions so direct checks respect loan stage restrictions.
    const permissions = this.getPermissionsForRole(role, _stage);
    return permissions.includes(permission);
  }

  public hasPermissionForAnyRole(roles: string[] | null | undefined, permission: Permission, stage?: string | null): boolean {
    if (!roles || !Array.isArray(roles) || roles.length === 0 || !permission) {
      return false;
    }

    return roles.some(role => this.hasPermission(role, permission, stage));
  }

  private normalizeRoleName(role: string): string {
    // Convert to title case for consistency with the JSON keys
    if (!role) return '';
    
    // Handle special case for "BROKER" -> "Broker"
    if (role.toUpperCase() === 'BROKER') {
      return 'Broker';
    }
    
    // Handle special case for "BORROWER" -> "Borrower"
    if (role.toUpperCase() === 'BORROWER') {
      return 'Borrower';
    }
    
    // Handle special case for "BORROWER_VIA_BROKER" -> "Borrower-via-Broker"
    if (role.toUpperCase() === 'BORROWER_VIA_BROKER') {
      return 'Borrower-via-Broker';
    }
    
    // Handle "Loan Officer", "Processor", "Underwriter", "Manager", "Admin"
    if (role.toUpperCase() === 'LOAN_OFFICER') {
      return 'Loan Officer';
    }

    if (role.toUpperCase() === 'VAL_DEV') {
      return 'Val Development';
    }
    
    // Default case: try to match the role name as is
    if (this.rolesPermissions[role]) {
      return role;
    }
    
    // If no match found, try title case conversion
    const titleCase = role.toLowerCase().split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    
    return titleCase;
  }
}
